
import { 
  FeatureBase,
  ModelBase,
} from "@models";
import { DataType, FeatureType } from "@dndModels/Types";

/**
 * This holds features that all Characters should have some form of at creation.
 * Unlike a regular FeatureSource, it's feature bases come with special limitations.
 * 
 * For example, all character should know some languages (Current rules says Common and 2 of their choice) 
 * from their race.
 * So there's a Languages fb, but it has a limitation that says no language features from their race.  
 * Older races always included some language features, but newer ones just rely on this general rule.
 * 
 * Another example is Ability Score Increases.  Older races specified these.  
 * Newer ones just said +2/+1 or +1/+1/+1 of your choice as a standard rule.  
 * Now the newest ones don't do it at all, and they've been moved to Backgrounds, 
 * but there's a rule that if both the race and background specify an ASI, use the race's ASI, 
 * and if neither do, then you get the +2/+1 or +1/+1/+1 of your choice.
 * 
 * Also older backgrounds didn't give a feat, but newer ones do, and there's a rule that if your 
 * background doesn't give you a feat then you should get an Origin Feat of your choice.
 * 
 * StandardFeatures is going to be made to work with all 3 of these, with their various scenarios.
 * 
 */
export class StandardFeatures extends ModelBase {
  data_type: DataType = "standard_features";
  feature_source_type = "StandardFeatures";
  feature_bases: FeatureBaseWithRules[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.feature_bases = [];
      if (obj.feature_bases && obj.feature_bases.length > 0) {
        for (const o of obj.feature_bases) {
          o.source_id = this._id;
          o.source_type = this.feature_source_type;
          const fb = new FeatureBaseWithRules(o);
          this.feature_bases.push(fb);
        }
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.feature_bases = [];
  //     if (obj.feature_bases && obj.feature_bases.length > 0) {
  //       for (const o of obj.feature_bases) {
  //         o.source_id = this._id;
  //         o.source_type = this.feature_source_type;
  //         const fb = new FeatureBaseWithRules(o);
  //         this.feature_bases.push(fb);
  //       }
  //     }
  //   }
  // }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      feature_bases: this.feature_bases.map(o => o.toDBObj())
    };
  }

  clone(drop_ids = false): StandardFeatures {
    const new_obj = new StandardFeatures();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: StandardFeatures, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.feature_bases = copyMe.feature_bases.map(o => o.clone(drop_ids));
  }

  copy5e(copyMe: any) {
    console.log(copyMe);
  }
}

export class FeatureBaseWithRules extends FeatureBase {
  feature_type: FeatureType = ""; // What type of feature is in this FB?  Only one allowed.  Currently only planning for ASI, Language, and Feat. I'm going to need to change all the ones that use a SpecialFeature to get their ASIs to use the new method.
  look_in: ("race" | "background")[] = []; // Where do we look for it? If only one listed, then any in the other are still used, just not affected by this. Language only looks in race and Feat only looks in background.
  dominant_feature_source: "race" | "background" | "" = ""; // If both are listed in look_in, and this feature_type is found in both, which do we use?  If empty then allow both. Otherwise this specifies which to take.

  constructor(obj?: any, is_new = false) {
    super(obj, is_new);
    if (obj) {
      this.feature_type = obj.feature_type ? obj.feature_type : "";
      this.look_in = obj.look_in ? obj.look_in : [];
      this.dominant_feature_source = obj.dominant_feature_source ? obj.dominant_feature_source : "";
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      feature_type: this.feature_type,
      look_in: this.look_in,
      dominant_feature_source: this.dominant_feature_source
    };
  }

  copy(copyMe: FeatureBase, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof FeatureBaseWithRules) {
      this.feature_type = copyMe.feature_type;
      this.look_in = [...copyMe.look_in];
      this.dominant_feature_source = copyMe.dominant_feature_source;
    }
  }

  clone(drop_ids = false): FeatureBaseWithRules {
    const new_obj = new FeatureBaseWithRules();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}